import AddIcon from "@mui/icons-material/Add";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import { OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";

const EMPTY_REGULATION = {
  applicationDate: "", articleNumbers: "", code: "", conformity: "compliant",
  issuingBody: "", nature: "", promulgationDate: "", summary: "",
};
const CONFORMITY_OPTIONS = [
  ["compliant", "Conforme"], ["partial", "Partiellement conforme"],
  ["non_compliant", "Non conforme"], ["not_evaluated", "Non évalué"],
];
const SEARCH_FIELDS = [
  "code", "nature", "summary", "promulgationDate", "applicationDate",
  "articleNumbers", "issuingBody", "conformity",
];
const COLUMNS = [
  ["Code", 120], ["Nature", 145], ["Résumé", 220],
  ["Date promulgation", 130], ["Date application", 130],
  ["N° Article", 130], ["Organisme", 150], ["Conformité", 135],
  ["PJ", 70], ["Suivi OI3", 105], ["Actions", 100],
];
const normalize = (value) => String(value || "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("fr");
const createId = () => `regulation-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const getConformityLabel = (value) =>
  CONFORMITY_OPTIONS.find(([key]) => key === value)?.[1] || "Non évalué";

const textareaSx = {
  backgroundColor: "#fff", border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "6px", boxSizing: "border-box", color: OUTIL_COLORS.text,
  fontFamily: "'DM Sans', sans-serif", fontSize: 11.5, lineHeight: 1.45,
  minHeight: 68, outline: "none", p: 1, resize: "vertical", width: "100%",
  "&:focus": { borderColor: OUTIL_COLORS.navy, boxShadow: "0 0 0 2px rgba(23,61,97,.12)" },
};
const goldButtonSx = {
  backgroundColor: OUTIL_COLORS.gold, color: OUTIL_COLORS.text,
  fontSize: 10, fontWeight: 700, textTransform: "none",
  "&:hover": { backgroundColor: "#c79c2f" },
};
const sectionSx = {
  backgroundColor: "#f8fafc", border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "8px", p: 1.4,
};

const FieldLabel = ({ children, htmlFor }) => (
  <Typography
    color="#526b84" component="label" display="block" fontSize={9.5}
    fontWeight={700} htmlFor={htmlFor} letterSpacing=".04em" mb={0.45}
    textTransform="uppercase"
  >
    {children}
  </Typography>
);
const InputField = ({ field, label, onChange, type = "text", value }) => (
  <Box>
    <FieldLabel htmlFor={`regulation-${field}`}>{label}</FieldLabel>
    <TextField
      fullWidth id={`regulation-${field}`}
      onChange={(event) => onChange(field, event.target.value)}
      size="small" type={type} value={value || ""}
    />
  </Box>
);
const FormSection = ({ children, icon, title }) => (
  <Paper elevation={0} sx={sectionSx}>
    <Typography
      color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={800}
      sx={{ borderBottom: `1px solid ${OUTIL_COLORS.border}`, mb: 1.2, pb: 0.75 }}
      textTransform="uppercase"
    >
      {icon}{title}
    </Typography>
    {children}
  </Paper>
);

const RegulationDialog = ({ form, onChange, onClose, onDelete, onSave, open }) => (
  <Dialog
    fullWidth maxWidth="md" onClose={onClose} open={open}
    PaperProps={{ sx: { borderRadius: "14px", maxHeight: "92vh" } }}
  >
    <DialogTitle sx={{ alignItems: "center", borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700, justifyContent: "space-between", px: 2.2, py: 1.4 }}>
      <Box component="span">
        <BalanceOutlinedIcon sx={{ color: "#d59a24", fontSize: 19, mr: 0.8, verticalAlign: "middle" }} />
        Veille réglementaire — Fiche texte
      </Box>
      <Button color="error" onClick={onClose} size="small" startIcon={<CloseIcon />} sx={{ textTransform: "none" }} variant="outlined">Fermer</Button>
    </DialogTitle>
    <DialogContent sx={{ p: "18px !important" }}>
      <Box sx={{ display: "grid", gap: 1.2 }}>
        <Paper elevation={0} sx={{ alignItems: "center", backgroundColor: "#edf3f8", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "7px", display: "flex", justifyContent: "space-between", px: 1.4, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
            {form.id ? "Modification de l'entrée sélectionnée" : "Nouveau texte réglementaire"}
          </Typography>
          {form.id && <Button color="error" onClick={onDelete} size="small" startIcon={<DeleteOutlineIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="contained">Supprimer cette entrée</Button>}
        </Paper>

        <FormSection icon={<AttachFileOutlinedIcon sx={{ fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Pièces jointes (0)">
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>Aucune pièce jointe</Typography>
          <Tooltip title="Ajout de pièce jointe disponible ultérieurement">
            <span><Button disabled size="small" startIcon={<AttachFileOutlinedIcon />} sx={{ fontSize: 10.5, mt: 0.5, textTransform: "none" }}>Ajouter une pièce jointe</Button></span>
          </Tooltip>
        </FormSection>

        <FormSection icon={<EditOutlinedIcon sx={{ color: "#d49b17", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Identification du texte">
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <InputField field="code" label="Code / référence" onChange={onChange} value={form.code} />
            <InputField field="issuingBody" label="Organisme émetteur" onChange={onChange} value={form.issuingBody} />
            <InputField field="nature" label="Nature du texte" onChange={onChange} value={form.nature} />
            <InputField field="articleNumbers" label="N° article(s) concerné(s)" onChange={onChange} value={form.articleNumbers} />
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <FieldLabel htmlFor="regulation-summary">Résumé / objet</FieldLabel>
              <Box
                aria-label="Résumé / objet" component="textarea" id="regulation-summary"
                onChange={(event) => onChange("summary", event.target.value)}
                rows={3} sx={textareaSx} value={form.summary || ""}
              />
            </Box>
          </Box>
        </FormSection>

        <FormSection icon={<CalendarMonthOutlinedIcon sx={{ color: "#3b82c4", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Dates et applicabilité">
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <InputField field="promulgationDate" label="Date de promulgation" onChange={onChange} type="date" value={form.promulgationDate} />
            <InputField field="applicationDate" label="Date d'application" onChange={onChange} type="date" value={form.applicationDate} />
          </Box>
        </FormSection>

        <FormSection icon={<CheckCircleOutlineIcon sx={{ color: "#25a769", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Conformité">
          <Box sx={{ maxWidth: { sm: "50%" } }}>
            <FieldLabel htmlFor="regulation-conformity">État de conformité</FieldLabel>
            <TextField fullWidth id="regulation-conformity" onChange={(event) => onChange("conformity", event.target.value)} select size="small" value={form.conformity || "compliant"}>
              {CONFORMITY_OPTIONS.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </TextField>
          </Box>
        </FormSection>
      </Box>
    </DialogContent>
    <DialogActions sx={{ borderTop: `1px solid ${OUTIL_COLORS.border}`, px: 2.2, py: 1.25 }}>
      <Button onClick={onClose} sx={{ color: OUTIL_COLORS.text, textTransform: "none" }}>Annuler</Button>
      <Button onClick={onSave} startIcon={<SaveOutlinedIcon />} sx={{ backgroundColor: "#27884f", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "#1f7040" } }} variant="contained">Valider et sauvegarder</Button>
    </DialogActions>
  </Dialog>
);

const VeilleReglementaire = ({ onChange, value = {} }) => {
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_REGULATION });
  const rows = useMemo(
    () => (Array.isArray(value.regulatoryRows) ? value.regulatoryRows : []),
    [value.regulatoryRows]
  );
  const filteredRows = useMemo(() => {
    const query = normalize(search).trim();
    return query
      ? rows.filter((row) => SEARCH_FIELDS.some((field) => normalize(row[field]).includes(query)))
      : rows;
  }, [rows, search]);

  const setRows = (nextRows) => onChange("regulatoryRows", nextRows);
  const openCreate = () => {
    setForm({ ...EMPTY_REGULATION });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY_REGULATION, ...row });
    setDialogOpen(true);
  };
  const saveRegulation = () => {
    setRows(form.id
      ? rows.map((row) => (row.id === form.id ? { ...form } : row))
      : [...rows, { ...form, id: createId() }]);
    setDialogOpen(false);
  };
  const deleteRegulation = () => {
    if (form.id) setRows(rows.filter((row) => row.id !== form.id));
    setDialogOpen(false);
  };

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Paper elevation={0} sx={{ alignItems: "center", backgroundColor: "#e8f0f1", border: `1px solid ${OUTIL_COLORS.border}`, borderLeft: `3px solid ${OUTIL_COLORS.gold}`, borderRadius: "7px", display: "flex", flexWrap: "wrap", gap: 1.2, px: 1.5, py: 1 }}>
        <BalanceOutlinedIcon sx={{ color: "#d59a24", fontSize: 18 }} />
        <Typography color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={700}>OUTIL_11_Veille_reglementaire.xlsx</Typography>
        <Tooltip title="Téléchargement disponible ultérieurement">
          <span><Button disabled size="small" startIcon={<FileDownloadOutlinedIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="contained">Télécharger le template</Button></span>
        </Tooltip>
      </Paper>

      <Paper aria-label="Veille réglementaire" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
        <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", justifyContent: "space-between", minHeight: 48, px: 1.75 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            <BalanceOutlinedIcon sx={{ color: "#d59a24", fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
            Veille réglementaire — §6.1
          </Typography>
          <Button onClick={openCreate} size="small" startIcon={<AddIcon />} sx={goldButtonSx} variant="contained">Ajouter</Button>
        </Box>
        <Box sx={{ alignItems: "center", backgroundColor: "#f5f8fb", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "6px", display: "flex", gap: 0.7, mx: 1.5, my: 1, px: 1.2, py: 0.8 }}>
          <DescriptionOutlinedIcon sx={{ color: OUTIL_COLORS.gold, fontSize: 16 }} />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>Cliquez sur une ligne pour ouvrir la fiche détail complète.</Typography>
        </Box>
        <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>RECHERCHE :</Typography>
          <TextField
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrer toutes les colonnes..." size="small"
            sx={{ flex: 1, minWidth: 220, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 10.5, height: 34 } }}
            value={search}
          />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>{filteredRows.length} / {rows.length} entrée(s)</Typography>
          <Button disabled={!search} onClick={() => setSearch("")} size="small" startIcon={<RestartAltIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}>Réinit.</Button>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table aria-label="Tableau de veille réglementaire" size="small" sx={{ minWidth: 1450, tableLayout: "fixed" }}>
            <TableHead><TableRow>{COLUMNS.map(([label, width]) => <TableCell key={label} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {!filteredRows.length ? (
                <TableRow><TableCell align="center" colSpan={COLUMNS.length} sx={{ color: "#8091a3", fontSize: 11, py: 2.2 }}>Aucun texte réglementaire enregistré.</TableCell></TableRow>
              ) : filteredRows.map((row) => {
                const values = [
                  row.code, row.nature, row.summary, row.promulgationDate,
                  row.applicationDate, row.articleNumbers, row.issuingBody,
                ];
                return (
                  <TableRow hover key={row.id} onClick={() => openEdit(row)} sx={{ cursor: "pointer", "&:nth-of-type(even)": { backgroundColor: "#f7f9fc" } }}>
                    {values.map((cell, index) => <TableCell key={COLUMNS[index][0]}>{cell || "—"}</TableCell>)}
                    <TableCell>
                      <Chip
                        label={getConformityLabel(row.conformity)}
                        size="small"
                        sx={{
                          backgroundColor: row.conformity === "compliant" ? "#d9f7e5" : row.conformity === "non_compliant" ? "#fde7e7" : "#fff2ce",
                          color: row.conformity === "compliant" ? "#147a43" : row.conformity === "non_compliant" ? "#a92824" : "#966413",
                          fontSize: 9.5, fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Entrée sauvegardée localement">
                          <IconButton aria-label="Entrée sauvegardée localement" onClick={(event) => event.stopPropagation()} size="small" sx={{ backgroundColor: "#27884f", borderRadius: "6px", color: "#fff" }}>
                            <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton aria-label="Modifier le texte réglementaire" onClick={(event) => { event.stopPropagation(); openEdit(row); }} size="small" sx={{ backgroundColor: OUTIL_COLORS.navy, borderRadius: "6px", color: "#fff" }}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <RegulationDialog
        form={form}
        onChange={(field, nextValue) => setForm((current) => ({ ...current, [field]: nextValue }))}
        onClose={() => setDialogOpen(false)}
        onDelete={deleteRegulation}
        onSave={saveRegulation}
        open={dialogOpen}
      />
    </Box>
  );
};

export default VeilleReglementaire;